import { Injectable } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { guid } from '@datorama/akita';
import { AkitaNgFormsManager } from '@datorama/akita-ng-forms-manager';
import { FormController } from '@lycoris-nubila/ngx-lycoris';
import { TranslateService } from '@ngx-translate/core';
import { SelectItem } from 'primeng/api';
import { Observable, combineLatest } from 'rxjs';
import { map, shareReplay, switchMap, tap } from 'rxjs/operators';

import { environment } from 'environments/environment';
import { OrganizationPropertyName } from 'organization/model/organization-property-name';
import { UserApplicationPort } from 'user/port/user-application.port';

@Injectable()
export class SidebarAddCoacheeController extends FormController<any> {
	public languageOptions$: Observable<SelectItem[]>;
	public organizationOptions$: Observable<SelectItem[]>;

	constructor(
		private _fb: FormBuilder,
		formsManager: AkitaNgFormsManager,
		private _translateService: TranslateService,
		private _userApplicationPort: UserApplicationPort
	) {
		super(
			formsManager,
			`sidebar-add-coachee-${guid()}`,
			_fb.group({
				lastName: ['', Validators.required],
				firstName: ['', Validators.required],
				language: [_translateService.currentLang],
				emailAddress: ['', [Validators.required, Validators.email]],
				organizationId: [null, Validators.required],
				welcomeMessage: [''],
			}),
			{ debounceTime: 250 }
		);

		this.initObservables();
	}

	private initObservables() {
		this.languageOptions$ = this._translateService
			.stream('presentation.app.languages')
			.pipe(
				map((languages) =>
					environment.availableLocales
						.filter((locale) => !!languages[locale.lang])
						.map(
							(locale) =>
								({
									label: languages[locale.lang],
									value: locale.lang,
								} as SelectItem)
						)
				),
				shareReplay({ bufferSize: 1, refCount: true })
			);

		this.organizationOptions$ = this._userApplicationPort
			.observeSessionUser()
			.pipe(
				switchMap((user) =>
					combineLatest([user.organization$, user.partners$]).pipe(
						map(([organization, partners]) => ({
							organizations: [organization, ...partners],
							user,
						}))
					)
				),
				map((data) =>
					data.organizations.filter(
						(o) =>
							!o.isPropertyTrue(OrganizationPropertyName.IS_FICTITIOUS, false) &&
							data.user.hasRightOnOrganization(o.id, 'COACHEE', 'CAN_CREATE')
					)
				),
				tap((organizations) =>
					this.form.patchValue({ organizationId: organizations[0]?.id })
				),
				map((organizations) =>
					organizations.map((o) => ({ label: o.name, value: o.id } as SelectItem))
				),
				shareReplay({ refCount: true, bufferSize: 1 })
			);
	}
}
